import { createContext, useContext, useEffect, useState } from 'react';

import Loading from '../components/shared/Loading/Loading';
import useAuth from '../hooks/useAuth';
import Company from '../interfaces/Company';
import {
  Subscription,
  SubscriptionContextType,
} from '../interfaces/Subscription';
import {
  companyHasCallCredits,
  fetchStripeSubscription,
  fetchUsage,
  UsageResponse,
} from '../services/billingService';
import { getCompany } from '../services/companyService';

export interface SubscriptionContextState extends SubscriptionContextType {
  company: Company | null;
}

const SubscriptionContext = createContext<SubscriptionContextState | undefined>(
  undefined
);

export const SubscriptionProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const [usage, setUsage] = useState<UsageResponse | null>(null);
  const [hasCredits, setHasCredits] = useState(false);
  const [loading, setLoading] = useState(true);
  const { currentUser, isLoading: isCurrentUserLoading } = useAuth();

  const fetchSubscription = async () => {
    if (currentUser) {
      let companyData: Company | null = null;

      try {
        if (currentUser.company_id) {
          const res = await getCompany(currentUser.company_id as string);
          companyData = (res as { message?: Company })?.message ?? null;
          setCompany(companyData);
        }
      } catch (error) {
        console.error('Failed to fetch user or company:', error);
        setLoading(false);
        return;
      }

      if (!companyData) {
        console.error('Not company found for user:', currentUser);
        setLoading(false);
        return;
      }

      // Capacity gates bringing agents online. It is derived from the non-billing
      // get_usage endpoint so members without MANAGE_BILLING (and enterprise
      // companies with no Stripe subscription) are evaluated correctly.
      const usageData = await fetchUsage(currentUser.company_id as string);
      setUsage(usageData);
      setHasCredits(
        companyHasCallCredits(usageData, {
          liveCallsNumber: companyData.live_calls_number,
        })
      );

      try {
        const subDetails = await fetchStripeSubscription(
          companyData.billing_customer_id ?? ''
        );

        setSubscription(subDetails ?? null);
      } catch (error) {
        console.error(
          'Failed to fetch subscription:',
          error,
          companyData.billing_customer_id
        );
      } finally {
        setLoading(false);
      }
    } else if (!isCurrentUserLoading) {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscription();
  }, [currentUser, isCurrentUserLoading]);

  if (loading) return <Loading />;

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        company,
        usage,
        hasCredits,
        loading,
        refreshSubscription: fetchSubscription,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error(
      'useSubscription must be used within a SubscriptionProvider'
    );
  }
  return context;
};
